import * as React from 'react';
import { cn } from '../../lib/utils';

export interface PageHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Page title — the largest text in the page. */
  title: React.ReactNode;
  /** Subtitle / description rendered under the title. */
  description?: React.ReactNode;
  /** Small uppercase label rendered above the title — section / context. */
  eyebrow?: React.ReactNode;
  /** Right-aligned action cluster (buttons, switchers). */
  actions?: React.ReactNode;
  /** Optional bottom slot — tabs, filters, breadcrumbs. */
  meta?: React.ReactNode;
}

/**
 * PageHeader — the canonical title block at the top of every page.
 *
 * Provides eyebrow → title → description on the left, an action cluster on
 * the right, and an optional `meta` row that hangs below (often used for
 * tab strips or filter bars).
 */
export const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  (
    { title, description, eyebrow, actions, meta, className, ...props },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn('flex flex-col gap-4 pb-5', className)}
        {...props}
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-6">
          <div className="min-w-0 flex-1">
            {eyebrow && (
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {eyebrow}
              </div>
            )}
            <h1 className="mt-1 truncate text-2xl font-semibold tracking-tight text-foreground">
              {title}
            </h1>
            {description && (
              <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground">
                {description}
              </p>
            )}
          </div>
          {actions && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {actions}
            </div>
          )}
        </div>
        {meta && <div className="border-t border-border pt-4">{meta}</div>}
      </div>
    );
  },
);
PageHeader.displayName = 'PageHeader';
